import React, { useEffect, useState } from "react";
import { ChevronDown, ChevronRight, BookOpen, Plus, Trash2, CheckCircle2, AlertTriangle } from "lucide-react";
import { api } from "@/lib/api";
import LoadingSpinner from "@/components/ui/LoadingSpinner";

interface Rule {
  title: string;
  body: string;
}

const AiGuidelines: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState<string | null>(null);
  const [baseGuideline, setBaseGuideline] = useState("");
  const [rules, setRules] = useState<Rule[]>([{ title: "", body: "" }]);
  const [showBase, setShowBase] = useState(false);
  const [conflict, setConflict] = useState<{
    conflictingRule?: string;
    conflictingL0Excerpt?: string;
    explanation?: string;
  } | null>(null);

  useEffect(() => {
    (async () => {
      try {
        const data = await api.get<{
          baseGuideline: string;
          rules: Array<{ title?: string; body: string }>;
        }>("/admin/ai-guideline");
        setBaseGuideline(data.baseGuideline ?? "");
        const loaded = (data.rules ?? []).map((r: { title?: string; body: string }) => ({
          title: r.title ?? "",
          body: r.body ?? "",
        }));
        // Newest-first for editing, same convention the old modal used.
        setRules(loaded.length ? [...loaded].reverse() : [{ title: "", body: "" }]);
      } catch {
        // leave empty on error
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const handleAddRule = () => {
    setRules((prev) => [{ title: "", body: "" }, ...prev]);
  };

  const handleRemoveRule = (index: number) => {
    setRules((prev) => {
      const next = prev.filter((_, idx) => idx !== index);
      return next.length > 0 ? next : [{ title: "", body: "" }];
    });
  };

  const handleRuleChange = (index: number, patch: Partial<Rule>) => {
    setRules((prev) => {
      const next = [...prev];
      next[index] = { ...next[index], ...patch };
      return next;
    });
  };

  const handleSave = async () => {
    setSaving(true);
    setConflict(null);
    try {
      // Reverse back to oldest-first before saving, matching how it's stored
      // and re-fetched, so relative order stays stable across edits.
      const cleanRules = [...rules]
        .reverse()
        .map((r) => ({ title: r.title.trim(), body: r.body.trim() }))
        .filter((r) => r.body);
      await api.put("/admin/ai-guideline", { rules: cleanRules });
      setSavedAt(
        new Date().toLocaleTimeString("en-IN", {
          hour: "2-digit",
          minute: "2-digit",
        }),
      );
    } catch (err: any) {
      if (err?.data?.code === "L0_CONFLICT") {
        setConflict(err.data.errors ?? {});
      }
      // other errors: silently fail — user can retry
    } finally {
      setSaving(false);
    }
  };

  const activeCount = rules.filter((r) => r.body.trim()).length;

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-xl sm:text-2xl font-bold text-gray-900 flex items-center gap-2">
          <BookOpen className="h-6 w-6 text-primary shrink-0" />
          AI Guidelines
        </h1>
        <p className="text-sm text-gray-500 mt-1">
          The instructions the AI Counselor follows for every student conversation.
        </p>
      </div>

      {loading ? (
        <div className="flex justify-center py-16">
          <LoadingSpinner size="md" />
        </div>
      ) : (
        <>
          {/* L1 — editable rules */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 sm:p-6">
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between mb-4">
              <div>
                <h2 className="text-sm font-semibold text-gray-900">
                  L1 Guidelines ({activeCount} active)
                </h2>
                <p className="text-xs text-gray-500 mt-0.5">
                  Extra instructions enforced for all student conversations, on top of L0.
                </p>
              </div>
              <button
                type="button"
                onClick={handleAddRule}
                className="w-full sm:w-auto inline-flex items-center justify-center gap-1.5 text-xs font-semibold text-primary bg-primary/10 hover:bg-primary/20 px-3 py-1.5 rounded-lg transition-colors"
              >
                <Plus className="h-3.5 w-3.5" />
                Add New Rule
              </button>
            </div>

            <div className="space-y-3">
              {rules.map((rule, idx) => (
                <div
                  key={idx}
                  className="p-3 bg-gray-50/80 border border-gray-200 rounded-lg group hover:border-primary/40 transition-colors"
                >
                  <div className="flex items-center gap-2 mb-2">
                    <span className="shrink-0 text-xs font-bold text-gray-500 bg-gray-200/80 px-2 py-1 rounded font-mono">
                      #{idx + 1}
                    </span>
                    <input
                      type="text"
                      value={rule.title}
                      onChange={(e) => handleRuleChange(idx, { title: e.target.value })}
                      placeholder="Optional title"
                      className="flex-1 min-w-0 rounded-md border border-gray-300 px-3 py-1.5 text-xs font-medium focus:outline-none focus:ring-2 focus:ring-primary/40 focus:border-primary bg-white"
                    />
                    <button
                      type="button"
                      onClick={() => handleRemoveRule(idx)}
                      title="Remove rule"
                      className="shrink-0 p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-md transition-colors"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                  <textarea
                    value={rule.body}
                    onChange={(e) => handleRuleChange(idx, { body: e.target.value })}
                    rows={2}
                    placeholder="e.g. Always answer direct fees/dates questions before asking anything."
                    className="w-full resize-y rounded-md border border-gray-300 px-3 py-1.5 text-xs focus:outline-none focus:ring-2 focus:ring-primary/40 focus:border-primary bg-white"
                  />
                </div>
              ))}
            </div>

            <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between pt-4 mt-4 border-t border-gray-100">
              <button
                type="button"
                onClick={() => void handleSave()}
                disabled={saving}
                className="w-full sm:w-auto px-4 py-2 rounded-lg text-white text-xs font-semibold bg-primary hover:bg-primary/90 disabled:opacity-50 inline-flex items-center justify-center gap-2 shadow-sm"
              >
                {saving && <LoadingSpinner size="sm" />}
                {saving ? "Saving L1 Guidelines..." : "Save L1 Guidelines"}
              </button>
              {savedAt && (
                <span className="text-xs text-green-600 font-medium inline-flex items-center gap-1">
                  <CheckCircle2 className="h-3.5 w-3.5 text-green-500" />
                  Saved at {savedAt}
                </span>
              )}
            </div>
          </div>

          {/* L0 — read-only, code-owned */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 sm:p-6">
            <button
              type="button"
              onClick={() => setShowBase((s) => !s)}
              className="flex items-center gap-1.5 text-sm font-semibold text-gray-900 hover:text-gray-700 transition-colors w-full"
            >
              {showBase ? (
                <ChevronDown className="h-4 w-4 text-gray-500 shrink-0" />
              ) : (
                <ChevronRight className="h-4 w-4 text-gray-500 shrink-0" />
              )}
              L0 Guidelines (read-only)
            </button>
            <p className="text-xs text-gray-500 mt-0.5 ml-6">
              Code-owned base instructions. Contact the Tech team if these need to change.
            </p>
            {showBase && (
              <pre className="mt-3 whitespace-pre-wrap break-words text-[11px] text-gray-600 bg-gray-50 border border-gray-200 rounded-lg p-3 font-mono leading-relaxed max-h-96 overflow-y-auto">
                {baseGuideline}
              </pre>
            )}
          </div>
        </>
      )}

      {/* L0 conflict modal */}
      {conflict && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl w-full max-w-md max-h-[85vh] flex flex-col shadow-2xl">
            <div className="flex items-center gap-2.5 px-5 py-4 border-b border-gray-200 shrink-0">
              <div className="p-2 bg-red-100 rounded-lg text-red-600 shrink-0">
                <AlertTriangle className="h-5 w-5" />
              </div>
              <h3 className="font-bold text-gray-900 text-base">
                Conflicts with an L0 Guideline
              </h3>
            </div>
            <div className="px-5 py-4 space-y-3 overflow-y-auto">
              {conflict.conflictingRule && (
                <div>
                  <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">
                    Your rule
                  </p>
                  <p className="text-sm text-gray-800 bg-gray-50 border border-gray-200 rounded-lg p-2.5">
                    {conflict.conflictingRule}
                  </p>
                </div>
              )}
              {conflict.conflictingL0Excerpt && (
                <div>
                  <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">
                    Conflicts with L0
                  </p>
                  <p className="text-sm text-gray-800 bg-red-50 border border-red-200 rounded-lg p-2.5">
                    {conflict.conflictingL0Excerpt}
                  </p>
                </div>
              )}
              {conflict.explanation && (
                <p className="text-sm text-gray-600">{conflict.explanation}</p>
              )}
              <p className="text-sm text-gray-600 pt-1 border-t border-gray-100">
                L0 guidelines are code-owned and can't be changed from here.
                Contact the Tech team if you need the L0 guideline updated.
              </p>
            </div>
            <div className="px-5 py-3.5 border-t border-gray-100 flex justify-end shrink-0">
              <button
                type="button"
                onClick={() => setConflict(null)}
                className="px-4 py-2 rounded-lg text-white text-xs font-semibold bg-primary hover:bg-primary/90"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AiGuidelines;
